import React, { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useB2BAuth } from "../B2BAuthContext/B2BAuthContext";
import B2BLoadingSpinner from "../B2BLoadingSpinner/B2BLoadingSpinner";
import "./B2BProtectedRoute.css";

interface B2BProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireProfile?: boolean;
}

const B2BProtectedRoute: React.FC<B2BProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireProfile = false,
}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    isAdmin,
    isApproved,
    isEmailVerified,
    profileCompleted,
  } = useB2BAuth();
  const location = useLocation();
  const isGcbPath = location.pathname.startsWith("/gcb");
  const basePath = isGcbPath ? "/gcb" : "/b2b";

  // Use synchronous check for profile completion (from state or user object)
  const hasCompletedProfileSync = (): boolean => {
    return profileCompleted || user?.profileComplete || false;
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="b2b-loading-container">
        <B2BLoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to={`${basePath}/login`}
        state={{ from: location }}
        replace
      />
    );
  }

  // CRITICAL: Check admin requirement FIRST before any user status checks
  // This ensures admin routes are completely isolated from user route logic
  const isAdminRoute =
    requireAdmin ||
    location.pathname.startsWith("/b2b/admin") ||
    location.pathname.startsWith("/gcb/admin");
  
  if (isAdminRoute) {
    // For admin routes, only check if user is admin
    if (!isAdmin()) {
      return (
        <Navigate
          to={`${basePath}/dashboard`}
          replace
        />
      );
    }
    // Admin is authenticated and is admin - allow access immediately
    // Skip all user status checks (profile, approval, email verification)
    return <>{children}</>;
  }

  // Below checks only apply to NON-admin routes
  // Check email verification (admins can bypass)
  if (!isEmailVerified() && !isAdmin()) {
    return (
      <Navigate
        to={`${basePath}/verify-email`}
        state={{ email: user?.email }}
        replace
      />
    );
  }

  // Profile routes:
  // - Allow initial profile completion before approval
  // - Allow editing ONLY after approval (no re-approval required)
  const isProfileCreateRoute =
    location.pathname === "/b2b/profile/create" ||
    location.pathname === "/gcb/profile/create";
  const isProfileEditRoute =
    location.pathname === "/b2b/profile/edit" ||
    location.pathname === "/gcb/profile/edit";
  const isProfileRoute = isProfileCreateRoute || isProfileEditRoute;

  if (isProfileRoute) {
    // Admins can always access
    if (isAdmin()) return <>{children}</>;

    const profileComplete = hasCompletedProfileSync();
    const approved = isApproved();

    // Edit route requires approval
    if (isProfileEditRoute) {
      if (approved) return <>{children}</>;
      // Not approved: if they haven't completed the profile yet, go to create; otherwise waiting approval.
      return (
        <Navigate
          to={profileComplete ? `${basePath}/waiting-approval` : `${basePath}/profile/create`}
          replace
        />
      );
    }

    // Create route is allowed only for initial completion OR for approved users (safe fallback)
    if (isProfileCreateRoute) {
      if (!profileComplete) return <>{children}</>;
      if (approved) return <>{children}</>;
      return (
        <Navigate
          to={`${basePath}/waiting-approval`}
          replace
        />
      );
    }
  }

  // If not on profile creation route, check if profile is complete
  // Use synchronous check to avoid Promise issues
  if (!hasCompletedProfileSync() && !isAdmin()) {
    return (
      <Navigate
        to={`${basePath}/profile/create`}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check if user is approved (admins bypass this check)
  // IMPORTANT: Only check approval if profile is complete AND user is not on profile creation/edit route
  // If profile is not complete, user should be on profile creation page (handled above)
  // If user is on profile creation/edit route, they should have access (handled above)
  if (hasCompletedProfileSync() && !isApproved() && !isAdmin() && !isProfileRoute) {
    return (
      <Navigate
        to={`${basePath}/waiting-approval`}
        replace
      />
    );
  }

  // Check profile completion requirement (admins can bypass this)
  // This is for routes that explicitly require profile (redundant check, but kept for explicit routes)
  if (requireProfile && !hasCompletedProfileSync() && !isAdmin()) {
    return (
      <Navigate
        to={`${basePath}/profile/create`}
        state={{ from: location }}
        replace
      />
    );
  }

  // Render children if authenticated (and admin if required, and profile completed if required)
  return <>{children}</>;
};

export default B2BProtectedRoute;

